import React from "react";

import { Link } from 'react-router-dom';
import { ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { IMenuItem } from '../types';
import { useDrawerContext } from '../contexts/drawer-context';

type Props = IMenuItem & {
  selected?: boolean;
  onClick?: () => void;
};

export const MenuItem: React.FC<Props> = ({
route,
  literal,
  Icon,
  selected,
  onClick,
}) => {
  const { isOpened, toggleIsOpened } = useDrawerContext();
  const { t } = useTranslation();
  
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    // 如果是移动端，点击菜单项后自动关闭菜单
    if (window.innerWidth <= 768) {
      toggleIsOpened(false);
    }
    
    // 调用原有的onClick回调
    if (onClick) {
      onClick();
    }
  };
  
  const link = (
    <ListItem
      button
      selected={selected}
      sx={{
        px: isOpened ? 2 : 2, // 减少展开时的左右内边距
        py: 1.5,
        height: '48px', // 固定高度，确保展开收起时高度一致
        margin: isOpened ? '4px 4px' : '4px 0px',
        borderRadius: 0, // 移除圆角
        justifyContent: isOpened ? 'flex-start' : 'center',
        alignItems: 'center', // 垂直居中
        transition: 'all 0.2s ease-in-out',
        overflow: 'hidden', // 防止高亮效果超出边界
        '&.Mui-selected': {
          backgroundColor: 'primary.main',
          color: 'white',
          borderRadius: 0, // 选择状态下也移除圆角
          boxShadow: isOpened ? 'inset 0 0 0 1px rgba(255, 255, 255, 0.5)' : '0 2px 4px rgba(25, 118, 210, 0.2)',
          '& .MuiListItemIcon-root': {
            color: 'white',
          },
          '&:hover': {
            backgroundColor: 'primary.dark',
            color: 'white', // 确保文字颜色保持白色
            transform: 'translateY(-1px)',
            borderRadius: 0, // 悬停状态下也移除圆角
            boxShadow: isOpened ? 'inset 0 0 0 1px rgba(255, 255, 255, 0.6)' : '0 4px 8px rgba(25, 118, 210, 0.3)',
            '& .MuiListItemIcon-root': {
              color: 'white', // 确保图标颜色保持白色
            },
          },
        },
        '&:hover': {
          backgroundColor: 'rgba(25, 118, 210, 0.16)',
          color: 'primary.main',
          transform: 'translateY(-1px)',
          '& .MuiListItemIcon-root': {
            color: 'primary.main',
          },
        },
      }}
      onClick={handleClick}
    >
      <ListItemIcon
        sx={[
          { 
            minWidth: 'auto',
            mr: isOpened ? 2 : 0,
            color: selected ? 'white' : 'text.secondary',
            transition: 'color 0.2s ease-in-out',
          },
        ]}
      >
        <Icon sx={{ fontSize: '1.25rem' }} />
      </ListItemIcon>
      {isOpened && (
        <ListItemText 
          primary={t(literal)} 
          sx={{
            '& .MuiTypography-root': {
              fontWeight: selected ? 600 : 500,
              fontSize: '0.9rem',
              transition: 'all 0.2s ease-in-out',
              whiteSpace: 'nowrap', // 防止文字换行
              overflow: 'hidden', // 隐藏溢出内容
              textOverflow: 'ellipsis', // 超出显示省略号
              maxWidth: '180px', // 限制最大宽度
            },
          }}
        />
      )}
    </ListItem>
  );

  return route
    ? <Link to={route} style={{ textDecoration: 'none', color: 'inherit' }}>{link}</Link>
    : link;
};
